import type { CargoType, CargoTypeSub } from '@prisma/client';

import { COMMODITY_TREE } from './canonical';
import type { CommodityNode } from './types';

/**
 * Lookups on the canonical commodity tree (`canonical.ts`): finding a commodity, walking up the tree,
 * and which commodities a customer can pick for a cargo type.
 */

type CargoSelection = { cargoType?: CargoType | null; cargoTypeSub?: CargoTypeSub | null };

/**
 * Lowercases a commodity string and collapses everything but letters and digits into single spaces, so
 * `"Fruit, Dry; Fresh"` and `"FRUIT DRY FRESH"` match. Used to match stored or typed values to the tree.
 */
export const normalizeCommodityKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const NODES_BY_ID = new Map(COMMODITY_TREE.map((node) => [node.id, node]));
const COMMODITIES = COMMODITY_TREE.filter((node) => node.level === 'ITEM');
// Descriptions first, then the aliases of merged commodities; the first one in tree order wins.
const COMMODITY_KEYS = [
  ...COMMODITIES.map((node) => [node.description, node] as const),
  ...COMMODITIES.flatMap((node) => (node.aliases ?? []).map((alias) => [alias, node] as const)),
];
const COMMODITIES_BY_DESCRIPTION = new Map<string, CommodityNode>();
const COMMODITIES_BY_KEY = new Map<string, CommodityNode>();
for (const [value, node] of COMMODITY_KEYS) {
  if (!COMMODITIES_BY_DESCRIPTION.has(value)) COMMODITIES_BY_DESCRIPTION.set(value, node);
  const key = normalizeCommodityKey(value);
  if (!COMMODITIES_BY_KEY.has(key)) COMMODITIES_BY_KEY.set(key, node);
}

/** Any node of the tree (group, sub-group or commodity) by its id, e.g. `tact_s0400` or `cmd_apples`. */
export const getCommodityNode = (id?: string): CommodityNode | undefined => (id ? NODES_BY_ID.get(id) : undefined);

/**
 * The commodity a request refers to. `request.commodity` stores the commodity's `description` (or, for
 * requests made before a merge, one of its `aliases`); this also accepts a commodity id. Tries the id,
 * then the exact description, then the normalized description. Returns undefined for free text that
 * matches nothing.
 */
export const findCommodityNode = (value?: string | null): CommodityNode | undefined => {
  if (!value) return;

  const byId = getCommodityNode(value);
  if (byId?.level === 'ITEM') return byId;

  return COMMODITIES_BY_DESCRIPTION.get(value) ?? COMMODITIES_BY_KEY.get(normalizeCommodityKey(value));
};

/**
 * What customers see for a stored commodity (`request.commodity`): the commodity's label, or the
 * value as typed when it is free text.
 */
export const getPrettyCommodity = (value: string) => findCommodityNode(value)?.label ?? value;

/**
 * The node itself, then each parent up to its TACT group: `[Apples, Fruit (Fresh), 0400–0499, 0001–0999]`.
 * Airline resolution and cargo availability both walk this chain and stop at the first match.
 */
export const getCommodityAncestors = (node: CommodityNode): CommodityNode[] => {
  const chain: CommodityNode[] = [];
  for (let current: CommodityNode | undefined = node; current; current = getCommodityNode(current.parentId)) {
    chain.push(current);
  }
  return chain;
};

/**
 * Whether a commodity can be picked for a cargo type + sub. The nearest `allowedCargo` up the tree
 * decides (child commodities inherit their parent's); with none anywhere, it is available for everything.
 * A request without a sub matches any entry for its cargo type.
 */
export const isCommodityAvailableFor = (commodity: CommodityNode, { cargoType, cargoTypeSub }: CargoSelection) => {
  const isAllowed = getCommodityAncestors(commodity).find((node) => node.allowedCargo)?.allowedCargo;
  if (!isAllowed) return true;

  return isAllowed.some((entry) => {
    const matchesCargoType = entry.cargoType === (cargoType ?? null);
    const matchesSub = !cargoTypeSub || !entry.cargoTypeSubs || entry.cargoTypeSubs.includes(cargoTypeSub);
    return matchesCargoType && matchesSub;
  });
};

/** Commodities with a `sortPosition` first (in that order), then the rest by label. */
const bySortPositionThenLabel = (a: CommodityNode, b: CommodityNode) => {
  if (a.sortPosition !== undefined && b.sortPosition !== undefined) return a.sortPosition - b.sortPosition;
  if (a.sortPosition !== undefined) return -1;
  if (b.sortPosition !== undefined) return 1;
  return a.label.localeCompare(b.label);
};

/** Every selectable commodity available for the cargo type + sub, in dropdown order. */
export const getCommodityNodesFor = ({
  cargoType,
  cargoTypeSub,
  excludeGeneric,
}: CargoSelection & { excludeGeneric?: boolean } = {}): CommodityNode[] =>
  COMMODITIES.filter(
    (node) =>
      node.isSelectable !== false &&
      !(excludeGeneric && node.isGeneric) &&
      isCommodityAvailableFor(node, { cargoType, cargoTypeSub }),
  ).sort(bySortPositionThenLabel);

/**
 * The commodity dropdown's options for a cargo type + sub (admin and customer request forms): `value` is
 * what `request.commodity` stores (the commodity's description), `label` is what customers see.
 */
export const getCommodityOptions = (selection: CargoSelection & { excludeGeneric?: boolean } = {}) =>
  getCommodityNodesFor(selection).map((node) => ({ value: node.description, label: node.label }));
